<!-- People asking to join a group (as someone already in it, or as someone new). The treasurer confirms or declines. -->
<script lang="ts">
  import { api } from "../lib/api.ts";
  import { app } from "../lib/store.svelte.ts";
  import { colorOf } from "../lib/colors.ts";
  import { shortDateTime } from "../lib/format.ts";

  interface Req {
    id: string;
    groupId: string;
    groupName: string;
    personId: string;
    personName: string;
    personColor: string;
    isNew?: boolean;
    userName: string;
    username: string | null;
    createdAt: string;
  }

  let { onchange }: { onchange?: (count: number) => void } = $props();
  let requests = $state<Req[]>([]);
  let busy = $state<string | null>(null);

  async function load() {
    try {
      requests = (await api<{ requests: Req[] }>("/api/admin/requests")).requests;
      onchange?.(requests.length);
    } catch {
      /* offline */
    }
  }
  $effect(() => {
    void load();
  });

  async function decide(r: Req, approve: boolean) {
    busy = r.id;
    try {
      await api(`/api/admin/requests/${r.id}`, { body: { approve } });
      app.toast(approve ? `${r.userName} is in ${r.groupName}` : `Declined ${r.userName}'s request`);
      await load();
    } catch (e) {
      app.toast((e as Error).message, "error");
    } finally {
      busy = null;
    }
  }
</script>

{#if requests.length}
  <section class="requests" aria-label="Join requests">
    <h2>{requests.length === 1 ? "1 person wants to join" : `${requests.length} people want to join`}</h2>
    {#each requests as r (r.id)}
      <div class="req" style:--c={colorOf(r.personColor)}>
        <span class="dot" aria-hidden="true"></span>
        <span class="what">
          {#if r.isNew}
            <span><strong>{r.userName}</strong> wants to join {r.groupName}.</span>
            <span class="muted small">Asked {shortDateTime(r.createdAt)}. Approving adds them to the group as {r.userName}.</span>
          {:else}
            <span><strong>{r.userName}</strong> says they're <strong>{r.personName}</strong> in {r.groupName}.</span>
            <span class="muted small">Asked {shortDateTime(r.createdAt)}. Approving lets their account act as {r.personName}.</span>
          {/if}
        </span>
        <span class="actions">
          <button class="btn small primary" disabled={busy === r.id} onclick={() => decide(r, true)}>Approve</button>
          <button class="btn small quiet" disabled={busy === r.id} onclick={() => decide(r, false)}>Decline</button>
        </span>
      </div>
    {/each}
  </section>
{/if}

<style>
  .requests {
    display: grid;
    gap: 8px;
    margin-bottom: 20px;
    padding: 14px;
    border-radius: var(--r-lg);
    background: color-mix(in srgb, #8b5cd6 10%, var(--surface));
    border: 1px solid color-mix(in srgb, #8b5cd6 30%, transparent);
  }

  h2 {
    margin: 0 0 4px;
    font-size: var(--t-md);
  }

  .req {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--surface);
  }

  .dot {
    flex: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--c);
  }

  .what {
    display: grid;
    gap: 2px;
    flex: 1 1 260px;
  }

  .small {
    font-size: var(--t-xs);
  }

  .actions {
    display: flex;
    gap: 6px;
  }
</style>
